import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import * as moment from 'moment';
import { AppService } from '@Core/services/proprietary/app.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { ContractHelperService } from './contract-helper.service';
import {
    SyncBrokerPartyAffiliateFromContractBrokerAffiliate,
    SyncBrokerPartyFromContractBroker,
    SyncProcedureEstFullTermPrem
} from './contract-object.actions';
import {
    AddAttachmentPointer,
    AddConditionPointer,
    AddContractInterestPointer,
    AddLinePointer,
    AddLocationPointer,
    AddNamePointer,
    AddPriorHistoryPointer,
    AddProfilePointer,
    AddTeamMemberPointer,
    RemoveAttachmentPointer,
    RemoveConditionPointer,
    RemoveContractInterestPointer,
    RemoveLinePointer,
    RemoveLocationPointer,
    RemoveNamePointer,
    RemovePriorHistoryPointer,
    RemoveProfilePointer,
    RemoveTeamMemberPointer,
    SetBillToEntity,
    SyncContractBroker,
    SyncContractBrokerAffiliate,
    SyncContractNumber,
    UpdateContract
} from './contract.actions';
import { UpdateParty } from './party.actions';
import { PartyState } from './party.state';

export interface ContractStateModel {
    contract: any;
    currentID: string;
}

@State<ContractStateModel>({
    name: 'contractState',
    defaults: {
        contract: {},
        currentID: null
    }
})
@Injectable()
export class ContractState {
    constructor(
        private helper: StateHelperService,
        private app: AppService,
        private store: Store,
        private contractHelperService: ContractHelperService
    ) { }

    @Selector()
    static contract(state: ContractStateModel) {
        return state.contract[state.currentID] || {};
    }
    @Selector()
    static currentID(state: ContractStateModel) {
        return state.currentID || '';
    }

    @Selector()
    static contracts(state: ContractStateModel) {
        let result: any = [];
        for (var i in state.contract) {
            result.push(state.contract[i]);
        }
        return result;
    }

    @Selector()
    static contractEffDate(state: ContractStateModel) {
        let result: any;
        if (state.contract[state.currentID].Revision_Eff_Date && state.contract[state.currentID].Revision_Eff_Date != '') {
            return state.contract[state.currentID].Revision_Eff_Date;
        } else {
            return state.contract[state.currentID].Eff_Date || '';
        }
    }

    @Selector()
    static isReporting(state: ContractStateModel) {
        let reporting = state.contract[state.currentID].Reporting_Ind;
        if (reporting && reporting == 'Y') {
            return true;
        } else {
            return false;
        }
    }

    @Action(AddLocationPointer)
    AddLocationPointer(sc: StateContext<ContractStateModel>, action: AddLocationPointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        if (!contract.Contract_Location) {
            contract.Contract_Location = [];
        }

        contract.Contract_Location = [...contract.Contract_Location, action.locID];

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(RemoveLocationPointer)
    RemoveLocationPointer(sc: StateContext<ContractStateModel>, action: RemoveLocationPointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        contract.Contract_Location = this.helper.removeArrayItemByValue(contract.Contract_Location, action.locID);

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(AddPriorHistoryPointer)
    AddPriorHistoryPointer(sc: StateContext<ContractStateModel>, action: AddPriorHistoryPointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        if (!contract.Prior_History) {
            contract.Prior_History = [];
        }

        contract.Prior_History = [...contract.Prior_History, action.PriorHistoryID];

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(RemovePriorHistoryPointer)
    RemovePriorHistoryPointer(sc: StateContext<ContractStateModel>, action: RemovePriorHistoryPointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        contract.Prior_History = this.helper.removeArrayItemByValue(contract.Prior_History, action.PriorHistoryID);

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(AddNamePointer)
    AddNamePointer(sc: StateContext<ContractStateModel>, action: AddNamePointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        if (!contract.Contract_Name) {
            contract.Contract_Name = [];
        }

        contract.Contract_Name = [...contract.Contract_Name, action.nameID];

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(RemoveNamePointer)
    RemoveNamePointer(sc: StateContext<ContractStateModel>, action: RemoveNamePointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        contract.Contract_Name = this.helper.removeArrayItemByValue(contract.Contract_Name, action.nameID);

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(AddTeamMemberPointer)
    AddTeamMemberPointer(sc: StateContext<ContractStateModel>, action: AddTeamMemberPointer) {
        const state = sc.getState();

        let contract = cloneDeep(state.contract[state.currentID]);

        if (!contract.Team_Members) {
            contract.Team_Members = {};
        }

        if (!contract.Team_Members.Team_Member) {
            contract.Team_Members.Team_Member = [];
        }

        contract.Team_Members.Team_Member = [...contract.Team_Members.Team_Member, action.partyID];

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(RemoveTeamMemberPointer)
    RemoveTeamMemberPointer(sc: StateContext<ContractStateModel>, action: RemoveTeamMemberPointer) {
        const state = sc.getState();

        let contract = cloneDeep(state.contract[state.currentID]);

        if (!contract.Team_Members) {
            contract.Team_Members = {};
        }

        if (!contract.Team_Members.Team_Member) {
            contract.Team_Members.Team_Member = [];
        }

        contract.Team_Members.Team_Member = this.helper.removeArrayItemByValue(
            contract.Team_Members.Team_Member,
            action.partyID
        );

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(AddConditionPointer)
    AddConditionPointer(sc: StateContext<ContractStateModel>, action: AddConditionPointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        if (!contract.Contract_Condition) {
            contract.Contract_Condition = [];
        }

        contract.Contract_Condition = [...contract.Contract_Condition, action.conditionID];

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(RemoveConditionPointer)
    RemoveConditionPointer(sc: StateContext<ContractStateModel>, action: RemoveConditionPointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        contract.Contract_Condition = this.helper.removeArrayItemByValue(contract.Contract_Condition, action.conditionID);

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(AddLinePointer)
    AddLinePointer(sc: StateContext<ContractStateModel>, action: AddLinePointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        if (!contract.Contract_Line) {
            contract.Contract_Line = [];
        }

        contract.Contract_Line = [...contract.Contract_Line, action.lineID];

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(RemoveLinePointer)
    removeLinePointer(sc: StateContext<ContractStateModel>, action: RemoveLinePointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        contract.Contract_Line = this.helper.removeArrayItemByValue(contract.Contract_Line, action.lineID);

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(AddContractInterestPointer)
    addContractInterestPointer(sc: StateContext<ContractStateModel>, action: AddContractInterestPointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        if (!contract.Contract_Interest) {
            contract.Contract_Interest = [];
        }

        contract.Contract_Interest = [...contract.Contract_Interest, action.interestID];

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(RemoveContractInterestPointer)
    removeContractInterestPointer(sc: StateContext<ContractStateModel>, action: RemoveContractInterestPointer) {
        const state = sc.getState();

        let contract = {
            ...state.contract[state.currentID]
        };

        contract.Contract_Interest = this.helper.removeArrayItemByValue(contract.Contract_Interest, action.interestID);

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(AddProfilePointer)
    AddProfilePointer(sc: StateContext<ContractStateModel>, action: AddProfilePointer) {
        const state = sc.getState();
        let contract = {
            ...state.contract[state.currentID]
        };

        if (!contract.Contract_Profile) {
            contract.Contract_Profile = [];
        }

        contract.Contract_Profile = [...contract.Contract_Profile, action.profileID];

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(RemoveProfilePointer)
    RemoveProfilePointer(sc: StateContext<ContractStateModel>, action: RemoveProfilePointer) {
        const state = sc.getState();
        let contract = {
            ...state.contract[state.currentID]
        };

        contract.Contract_Profile = this.helper.removeArrayItemByValue(contract.Contract_Profile, action.profileID);

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(AddAttachmentPointer)
    AddAttachmentPointer(sc: StateContext<ContractStateModel>, action: AddAttachmentPointer) {
        const state = sc.getState();
        let contract = {
            ...state.contract[state.currentID]
        };

        if (!contract.Contract_Attachment) {
            contract.Contract_Attachment = [];
        }

        contract.Contract_Attachment = [...contract.Contract_Attachment, action.attachmentID];

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(RemoveAttachmentPointer)
    RemoveAttachmentPointer(sc: StateContext<ContractStateModel>, action: RemoveAttachmentPointer) {
        const state = sc.getState();
        let contract = {
            ...state.contract[state.currentID]
        };

        contract.Contract_Attachment = this.helper.removeArrayItemByValue(
            contract.Contract_Attachment,
            action.attachmentID
        );

        sc.patchState({
            contract: {
                ...state.contract,
                [state.currentID]: contract
            }
        });
    }

    @Action(UpdateContract)
    updateContract(sc: StateContext<ContractStateModel>, action: UpdateContract) {
        const state = sc.getState();
        const contractID = state.currentID;
        const currentContract = state.contract[contractID];
        let updateContract: any = { ...action.contract };

        if (this.helper.stateValueChanged(updateContract.Eff_Date, currentContract.Eff_Date)) {
            let brokerParties = cloneDeep(this.store.selectSnapshot(PartyState.brokerParties));
            let supplierParties = cloneDeep(this.store.selectSnapshot(PartyState.supplierParties));
            for (var i in brokerParties) {
                brokerParties[i].agreementValidated = 'N';
                this.store.dispatch(new UpdateParty(brokerParties[i]));
            }
            for (var i in supplierParties) {
                supplierParties[i].agreementValidated = 'N';
                this.store.dispatch(new UpdateParty(supplierParties[i]));
            }
        }
        //Sync Incept_Date and Revision_Eff_Date when Eff_Date changes
        if (
            updateContract.Eff_Date &&
            updateContract.Eff_Date > '' &&
            currentContract.Eff_Date != updateContract.Eff_Date
        ) {
            let updatedEffDate = moment(updateContract.Eff_Date, 'YYYY-MM-DDTHH:mm:ssZ').utc().format('YYYY-MM-DDTHH:mm:ssZ');
            updateContract.Eff_Date = updatedEffDate;
            updateContract.Incept_Date = updateContract.Eff_Date;
            updateContract.Revision_Eff_Date = updateContract.Eff_Date;
        }

        //Recalc Exp_Date when Eff_Date or Term Changes
        if (
            updateContract.Eff_Date &&
            updateContract.Eff_Date > '' &&
            updateContract.Term &&
            updateContract.Term > '' &&
            (currentContract.Eff_Date != updateContract.Eff_Date || currentContract.Term != updateContract.Term)
        ) {
            updateContract.Exp_Date = moment(updateContract.Eff_Date, 'YYYY-MM-DDTHH:mm:ssZ')
                .utc()
                .add(updateContract.Term, 'months')
                .format('YYYY-MM-DDTHH:mm:ssZ');
            updateContract.Revision_Exp_Date = moment(updateContract.Eff_Date, 'YYYY-MM-DDTHH:mm:ssZ')
                .utc()
                .add(updateContract.Term, 'months')
                .format('YYYY-MM-DDTHH:mm:ssZ');
        }

        //Clear Affiliate if Broker Changed
        if (updateContract.Broker && currentContract.Broker.Entity_ID != updateContract.Broker.Entity_ID) {
            if (updateContract.Broker_Affiliate) {
                updateContract.Broker_Affiliate.Entity_ID = '';
                updateContract.Broker_Affiliate.Entity_Name = '';
            }
        }

        //Clear fields if dependency changed
        if (
            updateContract.Audit_Ind &&
            currentContract.Audit_Ind &&
            updateContract.Audit_Ind != currentContract.Audit_Ind &&
            updateContract.Audit_Ind == 'N'
        ) {
            updateContract.Audit_Term = '';
        }

        if (
            updateContract.Reporting_Ind &&
            currentContract.Reporting_Ind &&
            updateContract.Reporting_Ind != currentContract.Reporting_Ind &&
            updateContract.Reporting_Ind == 'N'
        ) {
            updateContract.Reporting_Freq = '';
        }

        if (
            updateContract.Inst_Bill_Ind &&
            currentContract.Inst_Bill_Ind &&
            updateContract.Inst_Bill_Ind != currentContract.Inst_Bill_Ind &&
            updateContract.Inst_Bill_Ind == 'N'
        ) {
            updateContract.InstPlan_Code = '';
        }

        if (
            updateContract.Countersign_Ind &&
            currentContract.Countersign_Ind &&
            updateContract.Countersign_Ind != currentContract.Countersign_Ind &&
            updateContract.Countersign_Ind == 'N'
        ) {
            updateContract.Countersign_State = '';
            updateContract.Countersign_Entity = { Entity_ID: '', Entity_Name: '' };
        }

        if (
            updateContract.Is_Subsidiary_Ind &&
            currentContract.Is_Subsidiary_Ind &&
            updateContract.Is_Subsidiary_Ind != currentContract.Is_Subsidiary_Ind &&
            updateContract.Is_Subsidiary_Ind == 'N'
        ) {
            updateContract.Parent_Org_Name = '';
        }

        if (
            updateContract.Settlement_Type &&
            currentContract.Settlement_Type &&
            updateContract.Settlement_Type != currentContract.Settlement_Type &&
            updateContract.Settlement_Type != 'CM'
        ) {
            updateContract.Retro_Date = '';
            updateContract.Retro_Date_Type = '';
        }

        if (updateContract.Est_Full_Term_Prem && updateContract.Est_Full_Term_Prem != currentContract.Est_Full_Term_Prem) {
            sc.dispatch(new SyncProcedureEstFullTermPrem(updateContract.Est_Full_Term_Prem));
        }

        //Supplier_Bill_Ind
        if (
            updateContract.Supplier_Bill_Ind &&
            currentContract.Supplier_Bill_Ind &&
            updateContract.Supplier_Bill_Ind != currentContract.Supplier_Bill_Ind
        ) {
            if (updateContract.Supplier_Bill_Ind == 'Y') {
                updateContract.Receivable_Role = 'SP';
            } else {
                if (currentContract.Bill_To_Role_Type == 'FC') {
                    updateContract.Receivable_Role = 'CN';
                } else {
                    updateContract.Receivable_Role = 'BT';
                }
            }
        }

        //Receivable_Role
        if (
            updateContract.Receivable_Role &&
            currentContract.Receivable_Role &&
            updateContract.Receivable_Role != currentContract.Receivable_Role
        ) {
            if (updateContract.Receivable_Role == 'BT' && currentContract.Bill_To_Role_Type == 'FC') {
                updateContract.Receivable_Role = currentContract.Receivable_Role; //Rejects the change... Should we prompt?
            } else {
                if (updateContract.Receivable_Role == 'SP') {
                    updateContract.Supplier_Bill_Ind = 'Y';
                } else {
                    updateContract.Supplier_Bill_Ind = 'N';
                }
            }
        }

        if (
            updateContract.Line_Code &&
            updateContract.Line_Code > '' &&
            currentContract.Line_Code != updateContract.Line_Code
        ) {
            updateContract.Program = '';
        }

        sc.patchState({
            contract: {
                ...state.contract,
                [contractID]: { ...currentContract, ...updateContract }
            }
        });

        this.checkForTaxFilerUpdates(currentContract, updateContract.Broker);

        // If Broker Changed in Contract State, sync with Broker Party record(s)
        if (updateContract.Broker && currentContract.Broker.Entity_ID != updateContract.Broker.Entity_ID) {
            sc.dispatch([
                new SyncBrokerPartyFromContractBroker(
                    { Entity_ID: currentContract.Broker.Entity_ID, Entity_Name: currentContract.Broker.Entity_Name },
                    { Entity_ID: updateContract.Broker.Entity_ID, Entity_Name: updateContract.Broker.Entity_Name }
                )
            ]);
        }

        // If Broker Affiliate Changed in Contract State, sync with Broker Party record(s)
        if (
            updateContract.Broker_Affiliate &&
            currentContract.Broker_Affiliate.Entity_ID != updateContract.Broker_Affiliate.Entity_ID
        ) {
            sc.dispatch([
                new SyncBrokerPartyAffiliateFromContractBrokerAffiliate(updateContract.Broker_Affiliate, updateContract.Broker)
            ]);
        }

        if (this.helper.stateValueChanged(updateContract.Contract_Num, currentContract.Contract_Num)) {
            sc.dispatch(new SyncContractNumber(updateContract.Contract_Num));
        }
    }

    @Action(SyncContractNumber)
    syncContractNumber(sc: StateContext<ContractStateModel>, action: SyncContractNumber) {
        const state = sc.getState();
        if (!state.currentID) {
            return;
        }
        const contractID = state.currentID;
        const currentContract = state.contract[contractID];
        if (currentContract.Contract_Num == action.contractNumber) {
            return;
        }

        let updateContract = cloneDeep(currentContract);
        updateContract.Contract_Num = action.contractNumber;

        sc.patchState({
            contract: {
                ...state.contract,
                [contractID]: { ...currentContract, ...updateContract }
            }
        });
    }

    @Action(SetBillToEntity)
    setBillToEntity(sc: StateContext<ContractStateModel>, action: SetBillToEntity) {
        const state = sc.getState();
        let entity = action.entity;
        let entityID = entity.Entity_ID;
        let entityName = entity.Entity_Name;
        const contractID = state.currentID;
        let updateContract: any = cloneDeep(state.contract[contractID]);

        updateContract.Bill_To_Entity.Entity_ID = entityID;
        updateContract.Bill_To_Entity.Entity_Name = entityName;

        if (updateContract.Bill_To_Role_Type != action.roleType) {
            updateContract.Bill_To_Role_Type = action.roleType;
        }

        sc.patchState({
            contract: {
                ...state.contract,
                [contractID]: { ...state.contract[contractID], ...updateContract }
            }
        });
    }

    @Action(SyncContractBroker)
    syncContractBroker(sc: StateContext<ContractStateModel>, action: SyncContractBroker) {
        const state = sc.getState();
        const contractID = state.currentID;
        const currentContract = state.contract[contractID];
        let updateContract: any = cloneDeep(state.contract[contractID]);
        updateContract.Broker.Entity_ID = action.broker.Entity_ID;
        updateContract.Broker.Entity_Name = action.broker.Entity_Name;
        updateContract.Broker_Affiliate = { Entity_ID: '', Entity_Name: '' }; //SyncContractBroker always clears affiliate

        sc.patchState({
            contract: {
                ...state.contract,
                [contractID]: { ...state.contract[contractID], ...updateContract }
            }
        });

        this.checkForTaxFilerUpdates(currentContract, updateContract.Broker);
    }

    @Action(SyncContractBrokerAffiliate)
    syncContractBrokerAffiliate(sc: StateContext<ContractStateModel>, action: SyncContractBrokerAffiliate) {
        const state = sc.getState();
        const contractID = state.currentID;
        let updateContract: any = cloneDeep(state.contract[contractID]);

        updateContract.Broker_Affiliate.Entity_ID = action.brokerAffiliate.Entity_ID;
        updateContract.Broker_Affiliate.Entity_Name = action.brokerAffiliate.Entity_Name;

        sc.patchState({
            contract: {
                ...state.contract,
                [contractID]: { ...state.contract[contractID], ...updateContract }
            }
        });
    }

    checkForTaxFilerUpdates(currentContract: any, newBroker: any) {
        if (
            newBroker &&
            newBroker.Entity_ID !== currentContract.Broker.Entity_ID &&
            currentContract.Tax_Filed_By_Role !== 'BK' &&
            newBroker.Entity_ID !== '' &&
            newBroker.Entity_ID !== '-1000'
        ) {
            this.contractHelperService.initializeService('Broker');
            this.contractHelperService.setTaxesFiledByIfBrokerIsTaxFiler(newBroker.Entity_ID);
        }
    }
}
